"use client"

import { useState, useEffect } from "react"
import { useMediaQuery } from "@mui/material"
import { getDailyOrderCounts, getDateRangeOrderCounts } from "../api"
import { motion, AnimatePresence } from "framer-motion"
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Card,
  CardContent,
  Chip,
  Grid,
  useTheme,
  IconButton,
  Tooltip,
  CircularProgress,
  InputAdornment,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  MenuItem,
} from "@mui/material"
import {
  Restaurant as RestaurantIcon,
  CalendarToday as CalendarIcon,
  Refresh as RefreshIcon,
  Analytics as AnalyticsIcon,
  DateRange as DateRangeIcon,
  Today as TodayIcon,
  NotificationsActive as NotificationIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material"
import axios from "axios"
import { toast } from "react-toastify"
import SkeletonLoader from "../components/SkeletonLoader"
import AnimatedCounter from "../components/AnimatedCounter"

const API_BASE_URL = process.env.REACT_APP_API_URL

// Helper function to get period icon
const getPeriodIcon = (period) => {
  if (!period) return "🕐"
  if (period.toLowerCase().includes("breakfast")) return "🍳"
  if (period.toLowerCase().includes("dinner")) return "🍛"
  if (period.toLowerCase().includes("evening")) return "🍿"
  return "🕐"
}

// Helper function to get color for item type
const getItemColor = (itemType) => {
  if (!itemType) return "#757575"
  if (itemType.toLowerCase() === "veg") return "#2e7d32"
  if (itemType.toLowerCase() === "non-veg") return "#ed6c02"
  // Generate consistent colors for other item types
  const hash = itemType.split("").reduce((acc, char) => char.charCodeAt(0) + acc, 0)
  const colors = [
    "#673ab7", // Purple
    "#2196f3", // Blue
    "#e91e63", // Pink
    "#009688", // Teal
    "#ff5722", // Deep Orange
    "#795548", // Brown
    "#607d8b", // Blue Grey
  ]
  return colors[hash % colors.length]
}

// Helper function to get background gradient for item type
const getItemBackground = (itemType) => {
  if (!itemType) return "linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%)"
  if (itemType.toLowerCase() === "veg") return "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)"
  if (itemType.toLowerCase() === "non-veg") return "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)"

  // Generate consistent backgrounds for other item types
  const hash = itemType.split("").reduce((acc, char) => char.charCodeAt(0) + acc, 0)
  const backgrounds = [
    "linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)", // Purple light
    "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)", // Blue light
    "linear-gradient(135deg, #fce4ec 0%, #f8bbd0 100%)", // Pink light
    "linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)", // Teal light
    "linear-gradient(135deg, #fbe9e7 0%, #ffccbc 100%)", // Deep Orange light
    "linear-gradient(135deg, #efebe9 0%, #d7ccc8 100%)", // Brown light
    "linear-gradient(135deg, #eceff1 0%, #cfd8dc 100%)", // Blue Grey light
  ]
  return backgrounds[hash % backgrounds.length]
}

// Helper function to determine current meal period and if notifications are allowed
const getCurrentMealPeriod = () => {
  const now = new Date()
  const hour = now.getHours()

  // Breakfast window: 8 AM to 11 AM
  if (hour >= 8 && hour < 11) {
    return { period: "Breakfast", isValid: true, timeWindow: "8:00 AM - 11:00 AM" }
  }

  // Dinner window: 7 PM to 10 PM
  if (hour >= 19 && hour < 22) {
    return { period: "Dinner", isValid: true, timeWindow: "7:00 PM - 10:00 PM" }
  }

  return { period: null, isValid: false, timeWindow: null }
}

// Helper function to get next valid time window
const getNextValidTimeWindow = () => {
  const now = new Date()
  const hour = now.getHours()

  if (hour < 8) {
    return "Next window: 8:00 AM - 11:00 AM (Breakfast)"
  } else if (hour >= 11 && hour < 19) {
    return "Next window: 7:00 PM - 10:00 PM (Dinner)"
  } else if (hour >= 22) {
    return "Next window: Tomorrow 8:00 AM - 11:00 AM (Breakfast)"
  }

  return "No active notification window"
}

const AdminOrderCounts = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [orderCounts, setOrderCounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [dateLoading, setDateLoading] = useState(false)
  const [error, setError] = useState(null)
  const [refreshing, setRefreshing] = useState(false)
  const today = new Date()
  const [selectedDate, setSelectedDate] = useState(today)
  const [dateError, setDateError] = useState("")
  const [totalCounts, setTotalCounts] = useState({
    totalOrders: 0,
    itemTypeCounts: {},
  })
  const [searchMode, setSearchMode] = useState("single") // "single" or "range"
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [rangeError, setRangeError] = useState("")

  // Notification states
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false)
  const [sendingNotification, setSendingNotification] = useState(false)
  const [currentMealPeriod, setCurrentMealPeriod] = useState(getCurrentMealPeriod())
  const [selectedPeriod, setSelectedPeriod] = useState('')
  const [availablePeriods, setAvailablePeriods] = useState([])
  const [loadingPeriods, setLoadingPeriods] = useState(false)

  // Fetch available periods from slots API
  const fetchAvailablePeriods = async () => {
    setLoadingPeriods(true)
    try {
      const token = localStorage.getItem("idToken")
      const response = await axios.get(`${API_BASE_URL}/admin/slots`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      
      const slotData = response.data.slot
      if (slotData && slotData.is_active && slotData.slots) {
        const periods = slotData.slots.map(slot => slot.name)
        setAvailablePeriods(periods)
      } else {
        setAvailablePeriods([])
      }
    } catch (error) {
      console.error('Error fetching slots:', error)
      setAvailablePeriods([])
    } finally {
      setLoadingPeriods(false)
    }
  }

  // Update meal period every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMealPeriod(getCurrentMealPeriod())
    }, 60000) // Update every minute

    return () => clearInterval(interval)
  }, [])

  // Fetch available periods when notification dialog opens
  useEffect(() => {
    if (notificationDialogOpen) {
      fetchAvailablePeriods()
    }
  }, [notificationDialogOpen])

  useEffect(() => {
    // Only auto-fetch for single date mode
    if (searchMode === "single") {
      fetchOrderCounts()
    }
  }, [selectedDate, searchMode])

  const fetchOrderCounts = async () => {
    setDateLoading(true)
    try {
      let response

      if (searchMode === "single") {
        // Single date mode - date in URL path
        const yyyy = selectedDate.getFullYear()
        const mm = String(selectedDate.getMonth() + 1).padStart(2, "0")
        const dd = String(selectedDate.getDate()).padStart(2, "0")
        const formattedDate = `${yyyy}-${mm}-${dd}`

        // Call API with date in URL path
        response = await getDailyOrderCounts(formattedDate)
      } else {
        // Date range mode - use query parameters
        const params = new URLSearchParams()
        if (startDate) params.append("startDate", startDate)
        if (endDate) params.append("endDate", endDate)

        const queryParams = params.toString() ? `?${params.toString()}` : ""
        response = await getDateRangeOrderCounts(queryParams)
      }

      const data = response.data || response
      setOrderCounts(Array.isArray(data) ? data : [])

      if (Array.isArray(data) && data.length > 0) {
        // Calculate total counts across all periods
        let totalOrderCount = 0
        const allItemCounts = {}

        data.forEach((item) => {
          // Add the total count for this period
          totalOrderCount += item.totalCount || 0

          // Process item counts
          if (item.itemCounts) {
            Object.entries(item.itemCounts).forEach(([itemType, count]) => {
              if (allItemCounts[itemType]) {
                allItemCounts[itemType] += count
              } else {
                allItemCounts[itemType] = count
              }
            })
          }
        })

        setTotalCounts({
          totalOrders: totalOrderCount,
          itemTypeCounts: allItemCounts,
        })
      } else {
        setTotalCounts({
          totalOrders: 0,
          itemTypeCounts: {},
        })
      }

      setLoading(false)
      setError(null)
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        // Clear auth and redirect to login
        toast.error("Session expired. Redirecting to login...")
        setTimeout(() => {
          localStorage.removeItem("idToken")
          window.location.href = "/login"
        }, 2000) // 2 seconds delay
        return
      }
      console.error("Error fetching order counts:", error)
      setError("Failed to fetch order counts.")
      setLoading(false)
    } finally {
      setTimeout(() => setDateLoading(false), 300)
    }
  }

  const handleSendNotification = async () => {
    setSendingNotification(true)
    try {
      const token = localStorage.getItem("idToken")
      const requestBody = selectedPeriod ? { period: selectedPeriod } : {}
      const response = await axios.post(
        `${API_BASE_URL}/admin/send-arrival-notification`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      )

      if (response.status === 200) {
        const data = response.data
        toast.success(`Notification sent successfully!`)
      }
    } catch (error) {
      console.error("Error sending notification:", error)

      if (error.response?.status === 400) {
        toast.error(error.response.data.message || "Invalid time for sending notifications")
      } else if (error.response?.status === 409) {
        toast.warning(error.response.data.message || "Notification already sent for this period")
      } else if (error.response?.status === 403) {
        toast.error("You are not authorized to send notifications")
      } else {
        toast.error("Failed to send notification. Please try again.")
      }
    } finally {
      setSendingNotification(false)
      setNotificationDialogOpen(false)
    }
  }

  const validateDate = (dateString) => {
    const date = new Date(dateString)

    if (isNaN(date.getTime())) {
      return "Please enter a valid date"
    }
    return ""
  }

  const validateDateRange = () => {
    // Allow either start date or end date or both
    if (!startDate && !endDate) {
      return "Please select at least one date (start or end date)"
    }

    // If both dates are provided, validate the order
    if (startDate && endDate) {
      const start = new Date(startDate)
      const end = new Date(endDate)

      if (start > end) {
        return "Start date must be before end date"
      }
    }

    return ""
  }

  const handleSearchModeChange = (mode) => {
    setSearchMode(mode)
    setRangeError("")
    setDateError("")
    setError(null)

    // Clear any existing data when switching to range mode
    if (mode === "range") {
      setOrderCounts([])
      setTotalCounts({
        totalOrders: 0,
        itemTypeCounts: {},
      })
      setLoading(false)
    }
  }

  const handleRangeSearch = async () => {
    const error = validateDateRange()
    setRangeError(error)

    if (!error) {
      setDateLoading(true)
      try {
        // Build query params with only provided dates
        const params = new URLSearchParams()
        if (startDate) params.append("startDate", startDate)
        if (endDate) params.append("endDate", endDate)

        const queryParams = params.toString() ? `?${params.toString()}` : ""
        const response = await getDateRangeOrderCounts(queryParams)

        const data = response.data || response
        setOrderCounts(Array.isArray(data) ? data : [])

        if (Array.isArray(data) && data.length > 0) {
          // Calculate total counts across all periods
          let totalOrderCount = 0
          const allItemCounts = {}

          data.forEach((item) => {
            // Add the total count for this period
            totalOrderCount += item.totalCount || 0

            // Process item counts
            if (item.itemCounts) {
              Object.entries(item.itemCounts).forEach(([itemType, count]) => {
                if (allItemCounts[itemType]) {
                  allItemCounts[itemType] += count
                } else {
                  allItemCounts[itemType] = count
                }
              })
            }
          })

          setTotalCounts({
            totalOrders: totalOrderCount,
            itemTypeCounts: allItemCounts,
          })
        } else {
          setTotalCounts({
            totalOrders: 0,
            itemTypeCounts: {},
          })
        }

        setError(null)
      } catch (err) {
        console.error("Error fetching order counts:", err)
        setError("Failed to fetch order counts.")
      } finally {
        setTimeout(() => setDateLoading(false), 300)
      }
    }
  }

  const handleStartDateChange = (event) => {
    setStartDate(event.target.value)
    setRangeError("")
  }

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value)
    setRangeError("")
  }

  const handleDateChange = (event) => {
    const dateValue = event.target.value

    // Allow typing without immediate validation
    if (dateValue.length < 10) {
      setDateError("")
      return
    }

    const error = validateDate(dateValue)
    setDateError(error)

    if (!error && dateValue) {
      setSelectedDate(new Date(dateValue))
    }
  }

  const handleTodayClick = () => {
    setSelectedDate(new Date())
    setDateError("")
  }

  const handleRefresh = async () => {
    setRefreshing(true)
    await fetchOrderCounts()
    setTimeout(() => setRefreshing(false), 500)
  }

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" }
    return new Date(dateString).toLocaleDateString(undefined, options)
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  }

  if (loading) {
    return (
      <Container maxWidth="xl">
        <SkeletonLoader type="stats" />
        <SkeletonLoader type="card" rows={3} />
      </Container>
    )
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <Paper
            sx={{
              p: 4,
              textAlign: "center",
              background: "linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)",
              border: "1px solid #ffcdd2",
            }}
          >
            <Typography variant="h6" color="error" gutterBottom>
              {error}
            </Typography>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.location.reload()}
              style={{
                background: "linear-gradient(135deg, #20d5d5 0%, #06d6a0 100%)",
                color: "white",
                border: "none",
                padding: "12px 24px",
                borderRadius: "12px",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "14px",
                marginTop: "16px",
              }}
            >
              Try Again
            </motion.button>
          </Paper>
        </motion.div>
      </Container>
    )
  }

  // Get all item types sorted by count
  const allItemTypes = Object.entries(totalCounts.itemTypeCounts).sort((a, b) => b[1] - a[1])

  return (
    <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 }, py: { xs: 1, sm: 2 }, pb: { xs: 10, sm: 2 } }}>
      <motion.div variants={containerVariants} initial="hidden" animate="visible">
        {/* Enhanced Header */}
        <motion.div variants={itemVariants}>
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              mb: 4,
              p: 4,
              background: "linear-gradient(135deg, #2c3e50 0%, #34495e 50%, #3d566e 100%)",
              borderRadius: 4,
              color: "white",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "radial-gradient(circle at 80% 20%, rgba(255,255,255,0.15) 0%, transparent 50%)",
                pointerEvents: "none",
              },
              "&::after": {
                content: '""',
                position: "absolute",
                top: "-50%",
                left: "-50%",
                width: "200%",
                height: "200%",
                background: "conic-gradient(from 180deg, transparent, rgba(255,255,255,0.08), transparent)",
                animation: "rotate 25s linear infinite",
                pointerEvents: "none",
              },
            }}
          >
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                  <AnalyticsIcon sx={{ fontSize: 48, color: "#20d5d5" }} />
                  <Box>
                    <Typography
                      variant="h3"
                      fontWeight={800}
                      sx={{
                        background: "linear-gradient(45deg, #fff 30%, #ffe0b2 90%)",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        mb: 1,
                      }}
                    >
                      Daily Order Analytics
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.9 }}>
                      View detailed order statistics and analytics
                    </Typography>
                  </Box>
                </Box>
                <Tooltip title="Refresh Data">
                  <IconButton
                    onClick={handleRefresh}
                    disabled={refreshing}
                    sx={{
                      color: "white",
                      position: "relative",
                      zIndex: 1,
                      "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                    }}
                  >
                    <motion.div
                      animate={refreshing ? { rotate: 360 } : {}}
                      transition={{ duration: 1, ease: "linear" }}
                    >
                      <RefreshIcon />
                    </motion.div>
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </motion.div>

        {/* Enhanced Date Selector with Range Option and Notification Button */}
        <motion.div variants={itemVariants}>
          <Paper
            sx={{
              p: 3,
              mb: 4,
              background: "linear-gradient(135deg, rgba(32, 213, 213, 0.05) 0%, rgba(6, 214, 160, 0.05) 100%)",
              border: "2px solid rgba(32, 213, 213, 0.2)",
              borderRadius: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                <motion.div
                  animate={{
                    scale: [1, 1.1, 1],
                    rotate: [0, 5, -5, 0],
                  }}
                  transition={{ duration: 3, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut" }}
                >
                  <Box
                    sx={{
                      background: "linear-gradient(135deg, #20d5d5 0%, #06d6a0 100%)",
                      borderRadius: "50%",
                      p: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 4px 15px rgba(32, 213, 213, 0.3)",
                    }}
                  >
                    <CalendarIcon sx={{ color: "white", fontSize: 24 }} />
                  </Box>
                </motion.div>
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: "#2c3e50" }}>
                    Select Analysis Period
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    Choose single date or date range for detailed analytics
                  </Typography>
                </Box>
              </Box>

              {/* Send Notification Button */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Tooltip
                  title="Send food arrival notification for any meal period"
                  arrow
                >
                  <span>
                    <motion.div
                      whileHover={currentMealPeriod.isValid ? { scale: 1.05 } : {}}
                      whileTap={currentMealPeriod.isValid ? { scale: 0.95 } : {}}
                    >
                      <Button
                        onClick={() => setNotificationDialogOpen(true)}
                        disabled={sendingNotification}
                        variant="contained"
                        sx={{
                          background: "linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)",
                          color: "white",
                          fontWeight: 700,
                          px: 3,
                          py: 1.5,
                          borderRadius: 2,
                          boxShadow: "0 4px 15px rgba(255, 107, 53, 0.3)",
                          "&:hover": {
                            boxShadow: "0 6px 20px rgba(255, 107, 53, 0.4)",
                          },
                          "&:disabled": {
                            background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                            color: "#94a3b8",
                            boxShadow: "none",
                          },
                        }}
                        startIcon={
                          sendingNotification ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <NotificationIcon />
                          )
                        }
                      >
                        {sendingNotification
                          ? "Sending..."
                          : isMobile ? "Send Alert" : "Send Notification"}
                      </Button>
                    </motion.div>
                  </span>
                </Tooltip>
              </Box>
            </Box>

            {/* Search Mode Toggle */}
            <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Chip
                  label="Single Day"
                  onClick={() => handleSearchModeChange("single")}
                  sx={{
                    px: 2,
                    py: 1,
                    fontWeight: 700,
                    fontSize: "14px",
                    background:
                      searchMode === "single"
                        ? "linear-gradient(135deg, #20d5d5 0%, #06d6a0 100%)"
                        : "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                    color: searchMode === "single" ? "white" : "#64748b",
                    border: searchMode === "single" ? "none" : "1px solid #cbd5e1",
                    boxShadow: searchMode === "single" ? "0 4px 15px rgba(32, 213, 213, 0.3)" : "none",
                    cursor: "pointer",
                    "&:hover": {
                      boxShadow: "0 6px 20px rgba(32, 213, 213, 0.25)",
                    },
                  }}
                />
              </motion.div>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Chip
                  label="Date Range"
                  onClick={() => handleSearchModeChange("range")}
                  sx={{
                    px: 2,
                    py: 1,
                    fontWeight: 700,
                    fontSize: "14px",
                    background:
                      searchMode === "range"
                        ? "linear-gradient(135deg, #20d5d5 0%, #06d6a0 100%)"
                        : "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                    color: searchMode === "range" ? "white" : "#64748b",
                    border: searchMode === "range" ? "none" : "1px solid #cbd5e1",
                    boxShadow: searchMode === "range" ? "0 4px 15px rgba(32, 213, 213, 0.3)" : "none",
                    cursor: "pointer",
                    "&:hover": {
                      boxShadow: "0 6px 20px rgba(32, 213, 213, 0.25)",
                    },
                  }}
                />
              </motion.div>
            </Box>

            {/* Single Date Mode */}
            {searchMode === "single" && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <TextField
                      type="date"
                      value={selectedDate.toISOString().split("T")[0]}
                      onChange={handleDateChange}
                      error={!!dateError}
                      helperText={dateError || "Select any date to view order analytics"}
                      sx={{
                        minWidth: 200,
                        "& .MuiOutlinedInput-root": {
                          background: "white",
                          borderRadius: 2,
                          fontWeight: 600,
                          transition: "all 0.2s ease",
                          "&:hover": {
                            transform: "translateY(-1px)",
                            boxShadow: "0 4px 12px rgba(32, 213, 213, 0.15)",
                          },
                          "&.Mui-focused": {
                            boxShadow: "0 6px 20px rgba(32, 213, 213, 0.25)",
                          },
                          "&.Mui-error": {
                            borderColor: "#ef4444",
                          },
                        },
                        "& .MuiFormHelperText-root": {
                          fontSize: "0.75rem",
                          marginTop: "4px",
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <DateRangeIcon sx={{ color: dateError ? "#ef4444" : "#20d5d5" }} />
                          </InputAdornment>
                        ),
                        endAdornment: dateLoading && (
                          <InputAdornment position="end">
                            <CircularProgress size={16} sx={{ color: "#20d5d5" }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </motion.div>

                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Tooltip title="Jump to Today" arrow>
                      <IconButton
                        onClick={handleTodayClick}
                        sx={{
                          background: "linear-gradient(135deg, #06d6a0 0%, #20d5d5 100%)",
                          color: "white",
                          width: 48,
                          height: 48,
                          boxShadow: "0 4px 15px rgba(6, 214, 160, 0.3)",
                          "&:hover": {
                            boxShadow: "0 6px 20px rgba(32, 213, 213, 0.4)",
                          },
                        }}
                      >
                        <TodayIcon />
                      </IconButton>
                    </Tooltip>
                  </motion.div>
                </Box>
              </motion.div>
            )}

            {/* Date Range Mode */}
            {searchMode === "range" && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <TextField
                      type="date"
                      label="Start Date"
                      value={startDate}
                      onChange={handleStartDateChange}
                      error={!!rangeError}
                      sx={{
                        minWidth: 180,
                        "& .MuiOutlinedInput-root": {
                          background: "white",
                          borderRadius: 2,
                          fontWeight: 600,
                          transition: "all 0.2s ease",
                          "&:hover": {
                            transform: "translateY(-1px)",
                            boxShadow: "0 4px 12px rgba(32, 213, 213, 0.15)",
                          },
                          "&.Mui-focused": {
                            boxShadow: "0 6px 20px rgba(32, 213, 213, 0.25)",
                          },
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarIcon sx={{ color: rangeError ? "#ef4444" : "#20d5d5" }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </motion.div>

                  <Typography variant="h6" sx={{ color: "#64748b", mx: 1 }}>
                    to
                  </Typography>

                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <TextField
                      type="date"
                      label="End Date"
                      value={endDate}
                      onChange={handleEndDateChange}
                      error={!!rangeError}
                      sx={{
                        minWidth: 180,
                        "& .MuiOutlinedInput-root": {
                          background: "white",
                          borderRadius: 2,
                          fontWeight: 600,
                          transition: "all 0.2s ease",
                          "&:hover": {
                            transform: "translateY(-1px)",
                            boxShadow: "0 4px 12px rgba(32, 213, 213, 0.15)",
                          },
                          "&.Mui-focused": {
                            boxShadow: "0 6px 20px rgba(32, 213, 213, 0.25)",
                          },
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarIcon sx={{ color: rangeError ? "#ef4444" : "#20d5d5" }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </motion.div>

                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      onClick={handleRangeSearch}
                      disabled={(!startDate && !endDate) || dateLoading}
                      variant="contained"
                      sx={{
                        background: "linear-gradient(135deg, #20d5d5 0%, #06d6a0 100%)",
                        color: "white",
                        fontWeight: 700,
                        px: 3,
                        py: 1.5,
                        borderRadius: 2,
                        boxShadow: "0 4px 15px rgba(32, 213, 213, 0.3)",
                        "&:hover": {
                          boxShadow: "0 6px 20px rgba(32, 213, 213, 0.4)",
                        },
                        "&:disabled": {
                          background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                          color: "#94a3b8",
                          boxShadow: "none",
                        },
                      }}
                      startIcon={dateLoading ? <CircularProgress size={16} color="inherit" /> : <AnalyticsIcon />}
                    >
                      {dateLoading ? "Searching..." : "Search Range"}
                    </Button>
                  </motion.div>
                </Box>

                {rangeError && (
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                    <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                      {rangeError}
                    </Alert>
                  </motion.div>
                )}
              </motion.div>
            )}

            {dateError && searchMode === "single" && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                  {dateError}
                </Alert>
              </motion.div>
            )}
          </Paper>
        </motion.div>

        {/* Notification Confirmation Dialog */}
        <Dialog open={notificationDialogOpen} onClose={() => setNotificationDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle
            sx={{
              background: "linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)",
              color: "white",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <NotificationIcon />
            Send Food Arrival Notification
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <DialogContentText sx={{ mb: 3, fontSize: "16px" }}>
              Select a meal period to send arrival notifications to all users who made requests for that period today.
            </DialogContentText>
            
            <TextField
              select
              fullWidth
              label="Select Meal Period"
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              disabled={loadingPeriods}
              helperText={loadingPeriods ? "Loading active periods..." : availablePeriods.length === 0 ? "No active periods available" : "Choose from today's active meal periods"}
              sx={{ mb: 3 }}
            >
              {availablePeriods.map((period) => (
                <MenuItem key={period} value={period}>
                  {period}
                </MenuItem>
              ))}
            </TextField>
            
            {selectedPeriod && (
              <Box
                sx={{
                  p: 2,
                  background: "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)",
                  borderRadius: 2,
                  border: "1px solid #ffcc02",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#e65100" }}>
                  📧 Notification Details:
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, color: "#bf360c" }}>
                  • <strong>Meal Period:</strong> {selectedPeriod}
                </Typography>
                <Typography variant="body2" sx={{ color: "#bf360c" }}>
                  • <strong>Date:</strong> {new Date().toLocaleDateString()}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 2 }}>
            <Button
              onClick={() => setNotificationDialogOpen(false)}
              sx={{
                color: "#64748b",
                fontWeight: 600,
                "&:hover": { backgroundColor: "#f1f5f9" },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendNotification}
              disabled={sendingNotification || !selectedPeriod}
              variant="contained"
              sx={{
                background: "linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)",
                color: "white",
                fontWeight: 700,
                px: 3,
                "&:hover": {
                  boxShadow: "0 6px 20px rgba(255, 107, 53, 0.4)",
                },
                "&:disabled": {
                  background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                  color: "#94a3b8",
                },
              }}
              startIcon={sendingNotification ? <CircularProgress size={16} color="inherit" /> : <NotificationIcon />}
            >
              {sendingNotification ? "Sending..." : selectedPeriod ? `Send ${selectedPeriod} Alert` : "Select Period"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Summary Cards */}
        <motion.div variants={itemVariants}>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Total Orders Card - Always first */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <motion.div whileHover={{ scale: 1.02 }} transition={{ type: "spring", stiffness: 300 }}>
                <Card
                  sx={{
                    background: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
                    border: "1px solid #90caf9",
                    height: "100%",
                  }}
                >
                  <CardContent sx={{ textAlign: "center", p: 3 }}>
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: "spring" }}
                    >
                      <RestaurantIcon sx={{ fontSize: 48, color: "#1976d2", mb: 2 }} />
                    </motion.div>
                    <AnimatedCounter
                      value={totalCounts.totalOrders}
                      variant="h3"
                      sx={{ fontWeight: 700, color: "#1976d2", mb: 1 }}
                    />
                    <Typography variant="h6" fontWeight={600} color="#0d47a1">
                      TOTAL ORDERS
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            {/* All Item Type Cards - Dynamic based on data */}
            {allItemTypes.map(([itemType, count], index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={itemType}>
                <motion.div whileHover={{ scale: 1.02 }} transition={{ type: "spring", stiffness: 300 }}>
                  <Card
                    sx={{
                      background: getItemBackground(itemType),
                      border: `1px solid ${getItemColor(itemType)}40`,
                      height: "100%",
                    }}
                  >
                    <CardContent sx={{ textAlign: "center", p: 3 }}>
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.3 + index * 0.1, type: "spring" }}
                      >
                        <RestaurantIcon sx={{ fontSize: 48, color: getItemColor(itemType), mb: 2 }} />
                      </motion.div>
                      <AnimatedCounter
                        value={count}
                        variant="h3"
                        sx={{ fontWeight: 700, color: getItemColor(itemType), mb: 1 }}
                      />
                      <Typography variant="h6" fontWeight={600} color={getItemColor(itemType)}>
                        {itemType.toUpperCase()}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}

            {/* Show message if no item data */}
            {allItemTypes.length === 0 && (
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Card sx={{ background: "#f5f5f5", height: "100%" }}>
                  <CardContent sx={{ textAlign: "center", p: 3 }}>
                    <Typography variant="h6" color="text.secondary">
                      No item data available
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </motion.div>

        {/* Session Details */}
        <motion.div variants={itemVariants}>
          <Typography variant="h5" fontWeight={600} sx={{ mb: 3, color: "text.primary" }}>
            Session-wise Breakdown
          </Typography>

          <AnimatePresence>
            {orderCounts.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
              >
                <Paper
                  sx={{
                    p: 6,
                    textAlign: "center",
                    background: "linear-gradient(135deg, #f9f9f9 0%, #e9ecef 100%)",
                    border: "2px dashed #dee2e6",
                  }}
                >
                  <motion.div
                    animate={{ y: [0, -10, 0] }}
                    transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY }}
                  >
                    <Typography variant="h1" sx={{ mb: 2 }}>
                      📊
                    </Typography>
                  </motion.div>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No order data available for this date
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try selecting a different date to view order counts
                  </Typography>
                </Paper>
              </motion.div>
            ) : (
              <Grid container spacing={3}>
                {orderCounts.map((item, index) => {
                  const pk = item.PK || ""
                  const [pkDate, pkPeriod] = pk.split("#")
                  const date = item.requestDate || pkDate
                  const period = item.period || pkPeriod
                  const totalCount = item.totalCount || 0
                  const itemCounts = item.itemCounts || {}

                  return (
                    <Grid item xs={12} md={6} lg={4} key={pk || index}>
                      <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ y: -8 }}
                      >
                        <Card
                          sx={{
                            height: "100%",
                            background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                            border: "1px solid #e2e8f0",
                            overflow: "hidden",
                          }}
                        >
                          {/* Card Header */}
                          <Box
                            sx={{
                              background: "linear-gradient(135deg, #2c3e50 0%, #34495e 100%)",
                              p: 2,
                              color: "white",
                            }}
                          >
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <Typography variant="h6" fontWeight={600}>
                                {formatDate(date)} - {period}
                              </Typography>
                              <Chip
                                label={`Total: ${totalCount}`}
                                sx={{
                                  background: "linear-gradient(135deg, #20d5d5 0%, #06d6a0 100%)",
                                  color: "white",
                                  fontWeight: 600,
                                }}
                              />
                            </Box>
                          </Box>

                          <CardContent sx={{ p: 3 }}>
                            {/* Dynamic Item Counts */}
                            {Object.entries(itemCounts).length > 0 ? (
                              Object.entries(itemCounts).map(([itemType, count], idx) => (
                                <motion.div
                                  key={itemType}
                                  whileHover={{ scale: 1.02 }}
                                  transition={{ type: "spring", stiffness: 300 }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      p: 2,
                                      mb: idx < Object.entries(itemCounts).length - 1 ? 2 : 0,
                                      borderRadius: 2,
                                      background: getItemBackground(itemType),
                                      border: `1px solid ${getItemColor(itemType)}40`,
                                    }}
                                  >
                                    <Typography variant="body1" fontWeight={600} color={getItemColor(itemType)}>
                                      {itemType}
                                    </Typography>
                                    <Chip
                                      label={count}
                                      sx={{
                                        background: `linear-gradient(135deg, ${getItemColor(itemType)} 0%, ${getItemColor(
                                          itemType,
                                        )}CC 100%)`,
                                        color: "white",
                                        fontWeight: 700,
                                        fontSize: "16px",
                                      }}
                                    />
                                  </Box>
                                </motion.div>
                              ))
                            ) : (
                              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
                                <Box
                                  sx={{
                                    mt: 2,
                                    p: 2,
                                    background: "#f5f5f5",
                                    borderRadius: 2,
                                    textAlign: "center",
                                  }}
                                >
                                  <Typography variant="body2" color="text.secondary">
                                    No orders for this session
                                  </Typography>
                                </Box>
                              </motion.div>
                            )}
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Grid>
                  )
                })}
              </Grid>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </Container>
  )
}

export default AdminOrderCounts
